import { useNavigate } from 'react-router-dom';
import { Briefcase, CalendarDays, Monitor } from 'lucide-react';
import { Button } from 'components/ui/button';
import { SessionInterface, SessionsDetailsInterface } from './PreviousSessionMenu';
import { UserDetailsListItem } from '../../model';
import { LoggedInUserDetails } from '../../reducers';
import { useLazyGetProgramQuestionsQuery, useLazyGetSessionFeedbackAnswersQuery } from '../../store/api/feedbackApi';
import { feedbackSessionQuestion } from '../../actions/feedbackQuestionAction';
import { feedbackSessionFormInFocus } from '../../actions/feedbackFormInFocusAction';
import { useDispatch } from 'react-redux';
import { updateSessionDetailForm, updateSessionFeedbackForm } from '../../actions/feedbackAnswerAction';
import moment from 'moment';
import _ from 'lodash';
import { trackMixpanelEvent } from '../../services/mixpanel';

interface RequiredSessionProps {
	sessionInfo: SessionInterface;
	loggedInUserDetail: LoggedInUserDetails;
	userListProfilesDetails: UserDetailsListItem[];
}

interface SubmittedSessionFeedbackFormInterface {
	question_id: number;
	question_category: string;
	text: string | string[] | null;
	value: number | null;
}

interface FeedbackProps {
	mentee: { submitted: string; feedback: SubmittedSessionFeedbackFormInterface[] }[];
	mentor: { submitted: string; feedback: SubmittedSessionFeedbackFormInterface[] }[];
}

export default function SubmittedSession({
	sessionInfo,
	loggedInUserDetail,
	userListProfilesDetails,
}: RequiredSessionProps) {
	const { rated } = sessionInfo;
	const photoUrl = `${process.env.REACT_APP_PROFILE_PHOTOS_BASE_URL}${process.env.REACT_APP_PROFILE_PHOTOS_S3_BASE_URL}${process.env.REACT_APP_PROFILE_PHOTOS_S3_DIRECTORY_URL}`;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const sortedRatedSession = _.orderBy(rated, [(session) => new Date(session.booking_time)], ['desc']);
	const [loadProgramQuestions] = useLazyGetProgramQuestionsQuery();
	const [loadSessionFeedbackAnswers] = useLazyGetSessionFeedbackAnswersQuery();

	const routeToSessionSummary = async (session: SessionsDetailsInterface, userRole: string) => {
		const ratedUser =
			session.group_details.mentee.id !== loggedInUserDetail.id
				? `${session.group_details.mentee.full_name}`
				: `${session.group_details.mentor.full_name}`;
		const startTime = moment(session.booking_start_time, 'h.mm A');
		const endTime = moment(session.booking_end_time, 'h.mm A');
		const duration = moment.duration(endTime.diff(startTime));
		const sessionMethod = session.session_method === 'Video Call' ? 0 : 1;
		const ratedParticipant = session.booking_participants.find(
			(participant) => participant.user_id !== loggedInUserDetail.id
		);
		const sessionDetails = {
			bookingId: session.id,
			ratedParticipantId: ratedParticipant?.id,
			programme: { label: session.program.label, value: session.program.value },
			sessionWith: { label: ratedUser, value: session.group_details },
			sessionType: sessionMethod,
			sessionDate: new Date(session.booking_time),
			sessionTime: new Date(session.booking_time),
			sessionDuration: { label: duration.humanize(), value: duration.asMinutes() },
			sessionService: { label: session.service.label, value: session.service.value },
		};

		dispatch(updateSessionDetailForm(sessionDetails));
		const questions = await loadProgramQuestions({ programId: session.program.value, userType: userRole }).unwrap();
		dispatch(feedbackSessionQuestion(questions));
		const feedback = await loadSessionFeedbackAnswers(session.id).unwrap();
		const sessionFeedback =
			userRole === 'mentee'
				? { submitted: feedback.mentor[0].submitted, feedback: feedback.mentor[0].feedback }
				: { submitted: feedback.mentee[0].submitted, feedback: feedback.mentee[0].feedback };
		dispatch(updateSessionFeedbackForm(sessionFeedback));
		dispatch(feedbackSessionFormInFocus(3));
		navigate('../new-session');
		trackMixpanelEvent('Review Previous Session Rating');
	};

	return (
		<div>
			{sortedRatedSession.map((session) => {
				const pendingRatingUserId =
					session.mentee_id === loggedInUserDetail.id ? session.mentor_id : session.mentee_id;
				const userRole = session.mentee_id !== loggedInUserDetail.id ? 'mentee' : 'mentor';
				const userProfileDetails = userListProfilesDetails.find((user) => user.id === pendingRatingUserId);

				return (
					<article className="mb-8 min-w-0 rounded-lg border p-3 shadow-md" key={session.id}>
						<div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-3 pb-2">
							<div className="flex justify-center">
								<img alt="" className="size-20 rounded-xl object-cover" src={`${photoUrl}/${userProfileDetails?.id}/${userProfileDetails?.profile_photo}`} />
							</div>
							<div className="min-w-0">
								<div className="feedback-username">{`${userProfileDetails?.firstname} ${userProfileDetails?.lastname ?? ''}`}</div>
								<div className="flex min-w-0 gap-2">
									<Briefcase aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-[#979797]" />
									<div className="feedback-description min-w-0 break-words">{`${userProfileDetails?.userInfos?.jobTitle ?? ''}${userProfileDetails?.userInfos?.company ? ` at ${userProfileDetails.userInfos.company}` : ''}`}</div>
								</div>
							</div>
						</div>
						<div className="flex items-center gap-3 pb-2">
							<CalendarDays aria-hidden="true" className="size-5 shrink-0 text-[#979797]" />
							<div className="session-date">{session.booking_date}</div>
						</div>
						<div className="flex items-center gap-3 pb-3">
							<Monitor aria-hidden="true" className="size-5 shrink-0 text-[#979797]" />
							<div className="session-program-name min-w-0">{session.program?.label}</div>
						</div>
						<div className="border-t pb-2 pt-4">
							<Button className="w-full" onClick={() => routeToSessionSummary(session, userRole)}>
								Review
							</Button>
						</div>
					</article>
				);
			})}
		</div>
	);
}
